import React, { Fragment, useEffect, useRef, useState } from 'react'

import { GitlabServicesMtsRuMtsMediaCorporateCorporateBackendProjectsProject as Projects } from 'api/models'
import classNames from 'classnames'
import { isEqual } from 'lodash'
import AnimateHeight from 'react-animate-height'
import { Empty, Loader, Paginator } from 'shared/components'
import {
  useActions,
  useQuery,
  useTypedSelector,
  useWindowSize,
} from 'shared/hooks'

import { CatalogFullItem, CatalogItem } from '../'
import { getProjectsParams, projectListSize } from '../utils'
import classes from './List.module.scss'
import { ProjectsApi } from 'api'

type active = {
  id: number
  item: Projects
}

type Props = {
  isMore: boolean
  setIsMore: (m: boolean) => void
}

const projectsApi = new ProjectsApi()

export const CatalogList = ({ isMore, setIsMore }: Props): JSX.Element => {
  const [query, setQuery] = useQuery()
  const [currentQuery, setCurrentQuery] =
    useState<{ [key: string]: string }>(query)

  const [active, setActive] = useState<active | null>(null)
  const [activePage, setActivePage] = useState(
    query?.page ? Number(query?.page) : 1
  )
  const [activeFromQuery, setActiveFromQuery] = useState(false)
  const [isScrollToTop, setScrollToTop] = useState(false)
  const { catalog, loading, total } = useTypedSelector(state => state.catalog)
  const [loadingOpened, setLoadingOpened] = useState(false)
  const { fetchProjects } = useActions()
  const { isSmallDesktop, isTablet, isMobile } = useWindowSize()
  const containerRef = useRef<HTMLDivElement>(null)

  const emptyItem = {} as Projects

  useEffect(() => {
    if (query?.id) {
      setLoadingOpened(true)
      findOpenedProject()
    } else {
      fetchProjects(false, getProjectsParams(query))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const _currentQuery = getQueryWithoutId(currentQuery)
    const _query = getQueryWithoutId(query)

    if (!isEqual(_currentQuery, _query)) {
      // при применении любого фильтра (но не при пагинации) скроллим к началу каталога
      // скролл при пагинации обрабатывается в changePage
      currentQuery?.page === query?.page && scrollToTop()
      setActivePage(Number(query?.page) || 1)
      setCurrentQuery(query)
      fetchProjects(isMore, getProjectsParams(query))
      setScrollToTop(true)
    } else if (query?.draftOpen) {
      setActive({ id: 0, item: catalog[0] })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuery, fetchProjects, query])

  useEffect(() => {
    if (activeFromQuery) {
      setActiveFromQuery(false)
      const idx = catalog.findIndex(el => el.uid === query.id)

      if (idx > -1) {
        const params = getParams(idx + 1)
        setActive({ id: params.id, item: catalog[idx] })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog])

  useEffect(() => {
    if (!loading && isScrollToTop) {
      setActive(null)
      setScrollToTop(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  const getQueryWithoutId = (query: { [key: string]: string }) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, draftOpen, draftListId, ...rest } = query
    return rest
  }

  const findOpenedProject = () => {
    // поиск проекта, карточка которого должна быть сразу открытой
    // для просмотра draft проекта - этот метод не вызывается, т.к. мы знаем, что draft проект всегда будет первым в массиве
    // при клике на карточку из слайдера originals на главной в query передается id проекта
    // если он передан - запрашиваем 100 элементов и ищем, на какой странице будет находиться выбранный проект
    // костыль, избавиться от которого можно с доработками на бэке :(
    projectsApi
      .getProjectsListPublished(100)
      .then(res => {
        const d = res?.data?.data || []
        const elem = d?.findIndex((el: any) => el.uid === query.id)

        if (elem > -1) {
          const page = Math.ceil(elem / projectListSize)
          fetchProjects(false, getProjectsParams(query, undefined, page))
          setActiveFromQuery(true)
          setActivePage(page)
        } else {
          fetchProjects(false, getProjectsParams(query))
        }
      })
      .catch(() => {
        fetchProjects(false, getProjectsParams(query))
      })
      .finally(() => setLoadingOpened(false))
  }

  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const changePage = (value: number, more = false) => {
    setIsMore(more)
    setQuery({ ...query, page: `${value}`, id: '', draftOpen: '' })
    !more && scrollToTop()
  }

  const getDesktopParams = (index: number) => {
    const rest = index % 3
    const isRender = index !== 0 && rest === 0
    const r = rest === 0 ? 3 : rest
    const id = index - r
    return { isRender, id }
  }

  const getTabletParams = (index: number) => {
    const rest = index % 4
    const isRender = index !== 0 && rest === 0
    const r = rest === 0 ? 4 : rest
    const id = index - r
    return { isRender, id }
  }

  const getMobileParams = (index: number) => {
    const rest = index % 2
    const isRender = index !== 0 && rest === 0
    const r = rest === 0 ? 2 : rest
    const id = index - r
    return { isRender, id }
    // const isRender = true
    // const id = index
    // return { isRender, id }
  }

  const getParams = (index: number) => {
    if (isMobile) return getMobileParams(index)
    if (isSmallDesktop || isTablet) return getTabletParams(index)
    return getDesktopParams(index)
  }

  const renderFullContainer = (index: number, item: Projects) => {
    const length = catalog?.length
    const params = getParams(index)

    return render(item, index === length || params.isRender, params.id)
  }

  const handlerClick = (id: number, item: Projects) => {
    if (active?.item.uid === item.uid) {
      setQuery({ ...query, id: '', draftOpen: '' })
      return setActive(null)
    }

    setActive({ id, item })
    // const el = document.getElementById(getID(item.id))
    // el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // const getID = (id: string) => `catalog-active${id}`

  const render = (item: Projects, isRender: boolean, id: number) => {
    const isActive = Boolean(active && active.id === id)

    return (
      <Fragment key={item.uid}>
        <div
          className={`${classes.item} col-xs-6 col-sm-3 col-md-3 col-lg-4`}
          // id={getID(item.id)}
          onClick={() => handlerClick(id, item)}
        >
          <CatalogItem item={item} isActive={item.uid === active?.item.uid} />
        </div>
        {isRender && (
          <div className={`${classes.item_full} col-xs-12`}>
            <AnimateHeight height={isActive ? 'auto' : 0} duration={500}>
              <CatalogFullItem
                item={active?.item || emptyItem}
                onClick={() => {
                  setActive(null)
                  setQuery({ ...query, id: '', draftOpen: '' })
                }}
                active={isActive && !isScrollToTop}
              />
            </AnimateHeight>
          </div>
        )}
      </Fragment>
    )
  }

  if (!loading && !loadingOpened && !catalog?.length) {
    return <Empty />
  }

  const cn = classNames([
    classes.container,
    { [classes.visible]: loading || loadingOpened },
  ])

  return (
    <div className={cn} ref={containerRef}>
      {(loading || loadingOpened) && <Loader size="full" />}
      <div className={`${classes.items_container} row`}>
        {catalog.map((item, itemIndex) =>
          renderFullContainer(itemIndex + 1, item)
        )}
      </div>
      <div className="mt-2 mb">
        <Paginator
          withBtn
          totalCount={total}
          showCount={projectListSize}
          initialPage={activePage}
          onChange={(currentPage, s, m) => changePage(currentPage, m)}
        />
      </div>
    </div>
  )
}
